import {
    useCallback, useEffect, useRef, useState, useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useApolloClient, gql } from '@apollo/client';
import { useQuery } from '@tanstack/react-query';
import ServiceManager from 'services/ServiceManager';
import { useSecurityTypeIndex } from 'hooks/useSecurityTypeIndex';
import AdapterError from 'errors/AdapterError';
import { adaptBenchmark } from 'adaptors/adaptBenchmark';
import { adaptBenchmarks } from 'adaptors/adaptBenchmarks';
import { notification } from 'ui-library';

const GET_BENCHMARK_DATA = gql`
    query GetBenchmarkData($positionId: Int!) {
        securities(
            where: { id: { eq: $positionId } }
        ){
            nodes {
                id name tradingPrice
            }
        }
    }
`;

export const useBenchmarks = (portfolio, isLoading, showFirst, loadInitially = true) => {
    const client = useApolloClient();
    const { t, i18n: { language } } = useTranslation();
    const [selected, setSelected] = useState('');
    const hasAutoSelected = useRef(false);
    const { typeId, isLoadingTypeId, errorTypeId } = useSecurityTypeIndex(loadInitially);

    const product = useMemo(() => portfolio.product || portfolio.productId, [portfolio]);

    const {
        data: benchmarkOptions,
        isLoading: isLoadingOptions,
        error: errorOptions,
        refetch: refetchSecurities,
    } = useQuery({
        queryKey: ['benchmarkSecurities', product, typeId, language],
        enabled: !!product && !!typeId && !isLoading && !isLoadingTypeId && loadInitially,
        retry: false,
        staleTime: 2 * 60 * 1000,
        queryFn: async () => {
            const filter = { SecurityTypeIds: [typeId], Page: 1, PageSize: 1000 };
            const params = { language };
            const response = await ServiceManager.instrumentsService('searchSecurities', [product, filter, params]) as any;

            try {
                return adaptBenchmarks(response.data, t);
            } catch (err) {
                throw new AdapterError(err);
            }
        },
    });

    useEffect(() => {
        if (hasAutoSelected.current || !benchmarkOptions?.length) return;
        if (showFirst || portfolio?.benchmarkId) {
            const found = benchmarkOptions.find((item) => item.value === portfolio?.benchmarkId);
            const initialValue = found?.value ?? (showFirst ? benchmarkOptions[1]?.value : '');

            if (initialValue) {
                setSelected(initialValue);
                hasAutoSelected.current = true;
            }
        }
    }, [benchmarkOptions, portfolio?.benchmarkId, showFirst]);

    const {
        data: benchmark,
        isLoading: isLoadingBenchmarkDetail,
        error: errorBenchmarkDetail,
    } = useQuery({
        queryKey: ['benchmarkDetail', selected, portfolio.currencyId, language],
        enabled: !!selected,
        retry: false,
        staleTime: 2 * 60 * 1000,
        queryFn: async () => {
            const paramsKPI = {
                InstrumentSet: {
                    CurrencyId: portfolio.currencyId,
                    Allocations: [{ InstrumentId: +selected, Allocation: 1 }],
                    AllocationType: 'Percentage',
                },
                Years: 10,
                CalculationInterval: 'Monthly',
            };
            const responses = await Promise.all([
                client.query({ query: GET_BENCHMARK_DATA, variables: { positionId: +selected } }),
                ServiceManager.performanceService('calculateKPIPerformance', [paramsKPI]) as any,
            ]);
            const securityData = responses[0].data?.securities?.nodes?.[0];
            const { Volatility, ExpectedReturn } = responses[1].data || {};

            try {
                return adaptBenchmark({ ...securityData, Volatility, ExpectedReturn }, { language });
            } catch (err) {
                throw new AdapterError(err);
            }
        },
    });

    useEffect(() => {
        if (errorBenchmarkDetail) {
            notification.open({
                content: (errorBenchmarkDetail as any)?.message || t('general.somethingWentWrong'),
                type: 'error',
            });
        }
    }, [errorBenchmarkDetail, t]);

    const onBenchmarkChange = useCallback((value: string) => {
        setSelected(value);
    }, []);

    return {
        benchmark,
        benchmarkOptions: benchmarkOptions ?? [{ label: t('clientDashboard.selectBenchmark'), value: '' }],
        isLoadingBenchmark: isLoadingOptions || isLoadingTypeId || (!!selected && isLoadingBenchmarkDetail),
        errorBenchmarks: errorOptions || errorTypeId || errorBenchmarkDetail || null,
        onBenchmarkChange,
        benchmarkSelected: selected,
        searchSecuritiesByType: refetchSecurities,
    };
};
